/**
 * Is this campaign fit to be switched on, and if not, what is missing.
 *
 * The server already refuses to activate a campaign with no subject, no sender
 * or no audience (`campaigns.routes.ts`, 422 `incomplete`). That refusal is
 * correct and stays, but as the ONLY signal it is a bad deal for the operator:
 * you fill in four tabs, press Activate, and only then learn that the one thing
 * you missed is on a tab you are not looking at. So the same conditions are
 * evaluated here and shown while the campaign is being edited.
 *
 * Two severities, deliberately not one:
 *   - `blocking` mirrors the server's own list. If one of these is open,
 *     Activate WILL fail, and the button says so before it is pressed.
 *   - `advised` is what the server permits and a person should not. An empty
 *     body sends an empty letter; a body with no unsubscribe link makes a
 *     commercial message that owes its reader a free way out and does not offer
 *     one. Neither is the server's call to refuse, and neither should go out
 *     unnoticed.
 *
 * Pure module - no React - so the rules can be tested without rendering a tab.
 */
import { UNSUBSCRIBE_PLACEHOLDER } from "@wa/core/unsubscribe";
import type { Campaign } from "../../../../lib/marketing";

/**
 * The token the sender replaces with this recipient's own opt-out URL.
 *
 * Re-exported from `@wa/core` rather than declared here, because the other half
 * of the contract lives in the API (`crm-marketing/lib/unsubscribe/message.ts`)
 * and two agreeing string literals in two applications is how a letter goes out
 * with the words `{{unsubscribe}}` printed where the link should be. The name
 * stays `UNSUB_TOKEN` so the editor and the tests that use it are untouched.
 */
export const UNSUB_TOKEN = UNSUBSCRIBE_PLACEHOLDER;

/** Which tab fixes it. The strip links there, because "subject is missing" is
 *  only useful next to a way to go and write one. */
export type CheckTab = "audience" | "message" | "sending";

export interface Check {
  /** i18n suffix: `marketing.ready.<id>` for the label. */
  id: "audience" | "subject" | "sender" | "body" | "unsubscribe";
  tab: CheckTab;
  ok: boolean;
  severity: "blocking" | "advised";
}

export interface Readiness {
  checks: Check[];
  /** Open checks the server would refuse on. Activate is impossible while > 0. */
  blocking: number;
  /** Open checks a person should look at first. Activate still works. */
  advised: number;
  /** Nothing open at all. */
  ready: boolean;
}

/** An audience object is present but selects nothing on purpose only in the
 *  register case, where an empty filter means "the whole mirror". Absence of the
 *  object itself is what the server checks, so that is what is mirrored here. */
export function readiness(c: Campaign): Readiness {
  const body = `${c.bodyHtml}\n${c.bodyText}`;

  const checks: Check[] = [
    { id: "audience", tab: "audience", severity: "blocking", ok: c.audience != null },
    { id: "subject", tab: "message", severity: "blocking", ok: c.subject.trim() !== "" },
    { id: "sender", tab: "message", severity: "blocking", ok: c.fromAddress.trim() !== "" },
    { id: "body", tab: "message", severity: "advised", ok: c.bodyText.trim() !== "" },
    { id: "unsubscribe", tab: "message", severity: "advised", ok: body.includes(UNSUB_TOKEN) },
  ];

  const open = checks.filter((k) => !k.ok);
  return {
    checks,
    blocking: open.filter((k) => k.severity === "blocking").length,
    advised: open.filter((k) => k.severity === "advised").length,
    ready: open.length === 0,
  };
}
